import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Search, Loader2, BadgeCheck, ArrowDownUp } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, LoadingState } from "@/components/states";
import { cn } from "@/lib/utils";
import { ApprovalCard } from "@/components/approvals/ApprovalCard";
import { TopicView } from "@/components/topics/TopicView";
import { usePermissions } from "@/hooks/usePermissions";

const PAGE = 25;

const TABS = [
  { id: "needs_my_review", label: "Needs My Review" },
  { id: "submitted_by_me", label: "Submitted by Me" },
  { id: "recently_reviewed", label: "Recently Reviewed" },
];

const EMPTY = {
  needs_my_review: "Nothing needs your review right now.",
  submitted_by_me: "You don't have any active submissions.",
  recently_reviewed: "No recent reviews yet.",
};

// Renders the canonical View surface for a subject (adapter — Topics today).
function SubjectView({ item, canArchive, onClose, onChanged }) {
  if (item.subject_type === "master_topic") {
    return <TopicView topicId={item.subject_id} canArchive={canArchive} onClose={onClose} onChanged={onChanged}
      onEdit={onClose} />;
  }
  return null;
}

export default function Approvals() {
  const perms = usePermissions();
  const canArchive = perms.has("archive_topic");

  const [tab, setTab] = useState("needs_my_review");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [counts, setCounts] = useState({ needs_my_review: 0 });
  const [q, setQ] = useState("");
  const [subjectType, setSubjectType] = useState("");
  const [statusF, setStatusF] = useState("");
  const [sort, setSort] = useState("");        // "" -> per-tab default
  const [viewer, setViewer] = useState(null);
  const debounce = useRef(null);

  const loadCounts = useCallback(() => {
    apiFetch("/approvals/counts").then(setCounts).catch(() => {});
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const buildParams = useCallback((offset) => {
    const p = new URLSearchParams({ view: tab, limit: String(PAGE), offset: String(offset) });
    if (q.trim()) p.set("q", q.trim());
    if (subjectType) p.set("subject_type", subjectType);
    if (tab === "submitted_by_me" && statusF) p.set("status", statusF);
    if (sort && tab !== "recently_reviewed") p.set("sort", sort);
    return p.toString();
  }, [tab, q, subjectType, statusF, sort]);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    apiFetch(`/approvals?${buildParams(0)}`)
      .then((r) => { setItems(r.items || []); setTotal(r.total || 0); })
      .catch((e) => toast.error("Could not load approvals", { description: e.message }))
      .finally(() => setLoading(false));
  }, [buildParams]);

  const paramsKey = useMemo(() => buildParams(0), [buildParams]);
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(), q ? 250 : 0);
    return () => clearTimeout(debounce.current);
  }, [paramsKey]); // eslint-disable-line

  const loadMore = () => {
    setLoadingMore(true);
    apiFetch(`/approvals?${buildParams(items.length)}`)
      .then((r) => { setItems((prev) => [...prev, ...(r.items || [])]); setTotal(r.total || 0); })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoadingMore(false));
  };

  // A decision was made inside the reused subject View — refresh queue + counts
  // silently (never blank the whole page) so a resolved item drops naturally.
  const onSubjectChanged = () => { load(true); loadCounts(); };

  const switchTab = (id) => { setTab(id); setStatusF(""); setSort(""); };

  const sortOptions = tab === "needs_my_review"
    ? [{ v: "oldest", l: "Oldest waiting first" }, { v: "newest", l: "Newest submitted" }]
    : [{ v: "newest", l: "Newest submitted" }, { v: "oldest", l: "Oldest first" }];
  const sortValue = sort || (tab === "needs_my_review" ? "oldest" : "newest");

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6" data-testid="approvals-page">
      <PageHeader eyebrow="Review" title="Approvals"
        subtitle="Everything waiting on your review — and what you've submitted — in one calm place." />

      {/* Tabs */}
      <div className="mb-4 flex flex-wrap gap-1.5 border-b border-border/70" data-testid="approvals-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => switchTab(t.id)} data-testid={`approvals-tab-${t.id}`}
            className={cn("relative -mb-px flex items-center gap-1.5 rounded-t-lg px-3.5 py-2 text-sm font-medium transition-colors",
              tab === t.id ? "border-b-2 border-accent text-foreground" : "text-muted-foreground hover:text-foreground")}>
            {t.label}
            {t.id === "needs_my_review" && counts.needs_my_review > 0 && (
              <span className="rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold leading-none text-accent-foreground" data-testid="approvals-needs-badge">
                {counts.needs_my_review}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title or submitter…" className="pl-9" data-testid="approvals-search" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={subjectType || "all"} onValueChange={(v) => setSubjectType(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[130px]" data-testid="approvals-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="master_topic">Topics</SelectItem>
            </SelectContent>
          </Select>
          {tab === "submitted_by_me" && (
            <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="approvals-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                <SelectItem value="pending">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
              </SelectContent>
            </Select>
          )}
          {tab !== "recently_reviewed" && (
            <Select value={sortValue} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-[190px]" data-testid="approvals-sort">
                <ArrowDownUp className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" /><SelectValue />
              </SelectTrigger>
              <SelectContent>{sortOptions.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      <p className="mb-3 text-[13px] text-muted-foreground" data-testid="approvals-count">
        {loading ? "Loading…" : items.length < total ? `Showing ${items.length} of ${total}` : `${total} ${total === 1 ? "item" : "items"}`}
      </p>

      {loading ? <LoadingState label="Loading approvals…" />
        : items.length === 0 ? (
          <EmptyState title={EMPTY[tab]}
            message={q ? "Try a different search." : tab === "needs_my_review" ? "When a teammate asks you to review something, it'll appear here." : "This space fills up as review activity happens."} />
        ) : (
          <>
            <div className="grid gap-2.5" data-testid="approvals-list">
              {items.map((it) => <ApprovalCard key={`${it.approval_id}-${it.my_decided_at || ""}`} item={it} variant={tab} onOpen={setViewer} />)}
            </div>
            {items.length < total && (
              <div className="mt-5 flex justify-center">
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="approvals-load-more">
                  {loadingMore ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Loading…</> : `Load more (${total - items.length})`}
                </Button>
              </div>
            )}
          </>
        )}

      {viewer && <SubjectView item={viewer} canArchive={canArchive}
        onClose={() => { setViewer(null); onSubjectChanged(); }} onChanged={onSubjectChanged} />}
    </div>
  );
}
